import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { format, subDays } from 'date-fns';
import { ClipboardList, RefreshCw } from 'lucide-react';
import DashboardLayout from '../components/DashboardLayout';
import { Badge, Button, Card, EmptyState, FormField, Input, Select, Spinner } from '../components/ui';
import { analyticsAPI } from '../services/api';

const toInputDate = (d) => format(d, 'yyyy-MM-dd');

const ACTION_OPTIONS = [
  { value: '', label: 'All actions' },
  { value: 'auth.login.success', label: 'Login success' },
  { value: 'auth.login.failure', label: 'Login failure' },
  { value: 'auth.logout', label: 'Logout' },
  { value: 'user.created', label: 'User created' },
  { value: 'user.updated', label: 'User updated' },
  { value: 'user.deactivated', label: 'User deactivated' },
  { value: 'role.created', label: 'Role created' },
  { value: 'role.updated', label: 'Role updated' },
  { value: 'role.deleted', label: 'Role deleted' },
  { value: 'booking.approved', label: 'Booking approved' },
  { value: 'booking.rejected', label: 'Booking rejected' },
  { value: 'payment.recorded', label: 'Payment recorded' },
  { value: 'config.slots.saved', label: 'Slot config saved' },
  { value: 'facility.updated', label: 'Facility updated' },
  { value: 'facility.created', label: 'Facility created' },
];

function actionVariant(action) {
  if (!action) return 'neutral';
  if (action.includes('failure') || action.includes('rejected') || action.includes('deleted') || action.includes('deactivated')) {
    return 'danger';
  }
  if (action.includes('approved') || action.includes('success') || action.includes('created')) {
    return 'success';
  }
  if (action.startsWith('config.') || action.startsWith('facility.')) {
    return 'info';
  }
  return 'neutral';
}

export default function AuditLogs() {
  const defaultTo = useMemo(() => new Date(), []);
  const defaultFrom = useMemo(() => subDays(defaultTo, 7), [defaultTo]);

  const [from, setFrom] = useState(() => toInputDate(defaultFrom));
  const [to, setTo] = useState(() => toInputDate(defaultTo));
  const [action, setAction] = useState('');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [payload, setPayload] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await analyticsAPI.getAuditLogs({ from, to, action: action || undefined, page, limit: 50 });
      if (!res.success) {
        setError(res.message || 'Failed to load audit logs');
        setPayload(null);
        return;
      }
      setPayload(res.data);
    } catch (e) {
      setError(e.message || 'Failed to load audit logs');
      setPayload(null);
    } finally {
      setLoading(false);
    }
  }, [from, to, action, page]);

  useEffect(() => {
    load();
  }, [load]);

  const totalPages = payload?.pagination
    ? Math.max(1, Math.ceil(payload.pagination.total / payload.pagination.limit))
    : 1;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-ga-text-primary flex items-center gap-2">
              <ClipboardList className="h-7 w-7 text-green-600" />
              Audit log
            </h1>
            <p className="text-ga-text-muted mt-1">
              Append-only trail of privileged actions. Super Admin only.
            </p>
          </div>
          <Button variant="secondary" onClick={load} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        <Card>
          <div className="flex flex-wrap gap-4 mb-6">
            <FormField label="From">
              <Input type="date" value={from} onChange={(e) => { setPage(1); setFrom(e.target.value); }} className="w-auto" />
            </FormField>
            <FormField label="To">
              <Input type="date" value={to} onChange={(e) => { setPage(1); setTo(e.target.value); }} className="w-auto" />
            </FormField>
            <FormField label="Action">
              <Select value={action} onChange={(e) => { setPage(1); setAction(e.target.value); }} className="w-auto min-w-[12rem]">
                {ACTION_OPTIONS.map((opt) => (
                  <option key={opt.value || 'all'} value={opt.value}>{opt.label}</option>
                ))}
              </Select>
            </FormField>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <Spinner label="Loading audit logs…" />
          ) : !payload?.items?.length ? (
            <EmptyState title="No audit entries" description="No logs match the selected filters." />
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="ga-table min-w-full divide-y divide-ga-border-subtle text-sm">
                  <thead className="bg-ga-bg-page">
                    <tr>
                      <th className="px-4 py-3 text-left font-medium text-ga-text-subtle">Time</th>
                      <th className="px-4 py-3 text-left font-medium text-ga-text-subtle">Action</th>
                      <th className="px-4 py-3 text-left font-medium text-ga-text-subtle">Actor</th>
                      <th className="px-4 py-3 text-left font-medium text-ga-text-subtle">Entity</th>
                      <th className="px-4 py-3 text-left font-medium text-ga-text-subtle">Facility</th>
                      <th className="px-4 py-3 text-left font-medium text-ga-text-subtle">IP</th>
                      <th className="px-4 py-3 text-left font-medium text-ga-text-subtle">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-ga-border-subtle">
                    {payload.items.map((row) => (
                      <tr key={row.id} className="hover:bg-ga-bg-page align-top">
                        <td className="px-4 py-3 whitespace-nowrap text-ga-text-primary">
                          {format(new Date(row.created_at), 'dd MMM yyyy HH:mm:ss')}
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant={actionVariant(row.action)}>{row.action}</Badge>
                        </td>
                        <td className="px-4 py-3 text-ga-text-primary">
                          {row.actor_name || row.actor_username || '—'}
                          {row.metadata?.superAdmin && (
                            <Badge variant="warning" className="ml-2">SA</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-ga-text-muted">
                          {row.entity_type ? `${row.entity_type}${row.entity_id ? ` #${row.entity_id}` : ''}` : '—'}
                        </td>
                        <td className="px-4 py-3 text-ga-text-muted">
                          {row.facility_name || (row.facility_id ? `#${row.facility_id}` : '—')}
                        </td>
                        <td className="px-4 py-3 text-ga-text-muted font-mono text-xs">
                          {row.ip_address || '—'}
                        </td>
                        <td className="px-4 py-3 text-xs text-ga-text-muted max-w-xs">
                          <pre className="whitespace-pre-wrap break-words">{JSON.stringify(row.metadata || {}, null, 0)}</pre>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="mt-4 flex items-center justify-between text-sm">
                <Button variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                  Previous
                </Button>
                <span className="text-ga-text-muted">
                  Page {page} of {totalPages} ({payload.pagination.total} entries)
                </span>
                <Button variant="secondary" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>
                  Next
                </Button>
              </div>
            </>
          )}
        </Card>
      </div>
    </DashboardLayout>
  );
}
